import { matchModelProviderByPrefix, type DiscoveredModel } from '@reflex/shared';

/**
 * Known model IDs that map to the OpenCode free-tier provider.
 */
const OPENCODE_HOSTED_MODEL_IDS: ReadonlySet<string> = new Set(['bigpickle']);

// ─────────────────────────────────────────────────────────────────────────────
// Model filtering
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Filter discovered models by provider ID.
 *
 * @param models - Array of discovered models
 * @param providerId - Provider ID to filter by, or undefined to return all
 * @returns Filtered array of models belonging to the specified provider
 */
export function filterModelsByProvider(
  models: DiscoveredModel[],
  providerId: string | undefined,
): DiscoveredModel[] {
  if (!providerId) return models;
  return models.filter((m) => m.providerId === providerId);
}

// ─────────────────────────────────────────────────────────────────────────────
// Admin defaults cascading resolution
// ─────────────────────────────────────────────────────────────────────────────

export interface EffectiveProviderOptions {
  /** Whether exactly one provider is enabled (forces that provider) */
  isSingleEnabledProvider: boolean;
  /** The single enabled provider ID when isSingleEnabledProvider is true */
  singleEnabledProviderId?: string;
  /** User's local in-progress selection (temporary filter state) */
  selectedProviderForFilter?: string;
  /** Currently saved default provider from policy */
  currentDefaultProvider?: string;
  /** Function to check if a provider is enabled */
  isProviderEnabled: (providerId: string) => boolean;
}

/**
 * Resolve the effective provider for admin defaults UI.
 *
 * Resolution order:
 * 1. If only one provider is enabled, use that (no choice)
 * 2. Otherwise, use the user's local selection if it's still enabled
 * 3. Otherwise, use the saved default if it's still enabled
 * 4. Otherwise, undefined (no provider selected)
 *
 * @returns The effective provider ID, or undefined if none selected
 */
export function resolveEffectiveAdminProvider(
  options: EffectiveProviderOptions,
): string | undefined {
  const {
    isSingleEnabledProvider,
    singleEnabledProviderId,
    selectedProviderForFilter,
    currentDefaultProvider,
    isProviderEnabled,
  } = options;

  if (isSingleEnabledProvider) {
    return singleEnabledProviderId;
  }

  if (selectedProviderForFilter && isProviderEnabled(selectedProviderForFilter)) {
    return selectedProviderForFilter;
  }

  if (currentDefaultProvider && isProviderEnabled(currentDefaultProvider)) {
    return currentDefaultProvider;
  }

  return undefined;
}

/**
 * Determine if a saved model should be displayed in the model dropdown.
 *
 * The saved model is only shown when it belongs to the currently selected
 * provider. If the user has switched providers, the saved model from a
 * different provider is hidden (returns undefined).
 *
 * @param currentDefaultModel - The saved default model ID
 * @param effectiveProvider - The currently effective provider (may be undefined)
 * @param models - Array of discovered models to look up provider ownership
 * @returns The model ID to display, or undefined if it shouldn't be shown
 */
export function resolveDisplayedModel(
  currentDefaultModel: string | undefined,
  effectiveProvider: string | undefined,
  models: DiscoveredModel[],
): string | undefined {
  if (!currentDefaultModel) return undefined;

  // When no provider filter is active, always show the saved model
  if (effectiveProvider === undefined) return currentDefaultModel;

  // Check if the saved model belongs to the effective provider
  const modelProvider = models.find((m) => m.id === currentDefaultModel)?.providerId;
  if (modelProvider === effectiveProvider) return currentDefaultModel;

  return undefined;
}

// ─────────────────────────────────────────────────────────────────────────────
// Dropdown sentinel value handling
// ─────────────────────────────────────────────────────────────────────────────

/** Default sentinel value for "no selection" in Select components */
export const DROPDOWN_NONE_SENTINEL = '__none__';

/**
 * Coerce a domain value to a dropdown value, using a sentinel for undefined/null.
 *
 * @param value - The domain value (may be undefined)
 * @param sentinel - The sentinel string to use for undefined values
 * @returns The value or the sentinel if undefined
 */
export function toDropdownValue(
  value: string | undefined | null,
  sentinel: string = DROPDOWN_NONE_SENTINEL,
): string {
  return value ?? sentinel;
}

/**
 * Coerce a dropdown value back to a domain value, returning undefined for the sentinel.
 *
 * @param dropdownValue - The value from the dropdown
 * @param sentinel - The sentinel string that represents "no selection"
 * @returns The value or undefined if it matches the sentinel
 */
export function fromDropdownValue(
  dropdownValue: string,
  sentinel: string = DROPDOWN_NONE_SENTINEL,
): string | undefined {
  return dropdownValue === sentinel ? undefined : dropdownValue;
}

// ─────────────────────────────────────────────────────────────────────────────
// Provider derivation (heuristic fallbacks)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Derive the provider ID from a model ID using heuristics, for the narrow
 * case where the discovery catalog (the authoritative source — populated
 * from each plugin's `multiModel.providers` and the keys the user has
 * configured) hasn't loaded or doesn't contain the id.
 *
 * Resolution order:
 * 1. Parse `provider/model` format (e.g., "openai/gpt-4o" → "openai")
 * 2. Check known OpenCode-hosted model IDs (e.g., "bigpickle" → "opencode")
 * 3. Match the model id against the shared `MODEL_PROVIDER_ID_PREFIXES` table
 * 4. Return `undefined` — caller decides how to surface "unknown" (do NOT
 *    default to a specific provider; that's how `codex-5.5-xhigh` ended up
 *    rendering as "Anthropic")
 *
 * Prefer `resolveProviderFromDiscoveredModels` whenever discovery data is
 * available.
 */
export function deriveProviderFromModelId(modelId: string): string | undefined {
  const slashIdx = modelId.indexOf('/');
  if (slashIdx > 0) {
    return modelId.substring(0, slashIdx);
  }

  if (OPENCODE_HOSTED_MODEL_IDS.has(modelId.toLowerCase())) {
    return 'opencode';
  }

  return matchModelProviderByPrefix(modelId);
}

/**
 * Resolve the provider ID for a model using discovery data.
 *
 * Returns `undefined` when the model is not in the discovered list, allowing
 * the caller to decide how to handle unknown models (disable submit, show hint, etc.).
 *
 * @param modelId - The model ID to resolve
 * @param discoveredModels - Array of discovered models with provider info
 * @returns The provider ID if found, or undefined
 */
export function resolveProviderFromDiscoveredModels(
  modelId: string,
  discoveredModels: DiscoveredModel[] | undefined,
): string | undefined {
  if (!discoveredModels) return undefined;
  const discovered = discoveredModels.find((m) => m.id === modelId);
  return discovered?.providerId;
}

/**
 * Resolve the provider ID for a model, falling back to the prefix heuristic
 * when discovery doesn't contain it.
 *
 * Returns `undefined` when neither discovery nor the heuristic can attribute
 * the id — callers should render that as an "unknown" state rather than
 * picking a default provider.
 *
 * @param modelId - The model ID to resolve
 * @param discoveredModels - Optional array of discovered models with provider info
 */
export function resolveProviderForModelWithFallback(
  modelId: string,
  discoveredModels: DiscoveredModel[] | undefined,
): string | undefined {
  return (
    resolveProviderFromDiscoveredModels(modelId, discoveredModels) ??
    deriveProviderFromModelId(modelId)
  );
}
